import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';

/**
 * Everything a plain `<input>` accepts — `data-*`, `aria-*`, `id`, `name`,
 * `onFocus`, … — passes straight through, minus the props this component owns.
 */
export interface NumericFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  // `onBlur` is also owned: `handleBlur` below is applied after `{...rest}`
  // in the spread, so a caller-supplied `onBlur` would type-check and then
  // silently do nothing (finding F-25).
  'value' | 'onChange' | 'step' | 'min' | 'max' | 'type' | 'onBlur'
> {
  /** `null` renders an empty box (with the placeholder) — for optional fields
   *  where blank means "use the default". */
  value: number | null;
  onChange: (v: number) => void;
  /** Fired when the user empties the box; omit for required fields (the box
   *  then snaps back to the last good value on blur). */
  onClear?: () => void;
  step?: string;
  min?: string;
  max?: string;
}

/**
 * A controlled numeric input that keeps a local string buffer so the user can
 * type intermediate states like "-", "-0", "-0." without the form swallowing
 * the value. The form field is only updated when the string parses to a valid
 * finite number.
 *
 * Drop-in replacement for `<Input type="number">` in react-hook-form fields
 * where negative or decimal values are expected.
 */
export function NumericField({
  value,
  onChange,
  step,
  min,
  max,
  className,
  disabled,
  placeholder,
  onClear,
  ...rest
}: NumericFieldProps) {
  const toText = (v: number | null) => (v === null ? '' : String(v));
  const [localValue, setLocalValue] = useState(() => toText(value));
  const lastPropValue = useRef<number | null>(value);

  // Sync from form → local when the form value changes externally (e.g.
  // programmatic setValue calls).
  useEffect(() => {
    if (value !== lastPropValue.current) {
      lastPropValue.current = value;
      setLocalValue(toText(value));
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocalValue(raw);
      if (raw.trim() === '' && onClear) {
        lastPropValue.current = null;
        onClear();
        return;
      }
      const parsed = parseFloat(raw);
      if (Number.isFinite(parsed)) {
        lastPropValue.current = parsed;
        onChange(parsed);
      }
    },
    [onChange, onClear],
  );

  const handleBlur = useCallback(() => {
    // On blur, if the local string isn't a valid number, reset to the last
    // known good form value.
    const parsed = parseFloat(localValue);
    if (!Number.isFinite(parsed)) {
      setLocalValue(toText(value));
    }
  }, [localValue, value]);

  return (
    <Input
      // Spread first so an explicitly-owned prop can never be clobbered by a
      // stray one of the same name coming in through `rest`.
      {...rest}
      type='text'
      inputMode='decimal'
      step={step}
      min={min}
      max={max}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}
